import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveBar } from '@nivo/bar';
import { Tooltip as InfoHint } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import theme from '../../theme';
import { MONTH_FULL_LIST } from '../../constants';
import { MONTHLY_INCOME_CHART_DESCRIPTION } from '../../constants/graphDescriptions';
import { getFranchiseSales } from '../../api/dashboard';

const INCOME_KEYS = ['My Sales', 'Office Sales', 'Target Sales'];

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const shortCurrency = (v: number) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (n === 0) {
    return '$0';
  }
  if (abs >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `$${(n / 1_000).toFixed(0)}K`;
  }
  return `$${Math.round(n)}`;
};

const niceMax = (maxValue: number) => {
  if (!maxValue || maxValue <= 0) {
    return 10;
  }
  const padded = maxValue * 1.08;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const step = magnitude / 2;
  return Math.ceil(padded / step) * step;
};

const monthLabel = (month: number, year: number) =>
  `${MONTH_FULL_LIST[month - 1]?.slice(0, 3)}'${String(year).slice(-2)}`;

interface MonthRow {
  month: number;
  year: number;
  mySales: number;
  officeSales: number;
  target: number;
}

type SalesStatus = 'ahead' | 'on-track' | 'behind' | 'none';

const STATUS_LABEL: Record<SalesStatus, string> = {
  ahead: 'Ahead of target',
  'on-track': 'On track',
  behind: 'Behind target',
  none: 'No target set',
};

const MonthlyIncomeChart = () => {
  const [rows, setRows] = React.useState<MonthRow[]>([]);

  React.useEffect(() => {
    getFranchiseSales()
      .then(({ data }) => {
        setRows(
          (data || []).map((d: any) => ({
            month: Number(d.month),
            year: Number(d.year),
            mySales: Number(d.mySales) || 0,
            officeSales: Number(d.sales) || 0,
            target: Number(d.projectedSales) || 0,
          })),
        );
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
  }, []);

  const barData = React.useMemo(
    () =>
      rows.map(d => ({
        label: monthLabel(d.month, d.year),
        'My Sales': d.mySales,
        'Office Sales': d.officeSales,
        'Target Sales': d.target,
      })),
    [rows],
  );

  const hasData =
    rows.length > 0 &&
    rows.some(
      d =>
        d.mySales !== 0 || d.officeSales !== 0 || d.target !== 0,
    );

  const maxY = React.useMemo(() => {
    const maxVal = rows.reduce(
      (acc, d) =>
        Math.max(acc, d.mySales, d.officeSales, d.target),
      0,
    );
    return niceMax(maxVal);
  }, [rows]);

  const summary = React.useMemo(() => {
    if (rows.length === 0) {
      return null;
    }
    const yearlyTarget = (rows[0]?.target || 0) * 12;
    const now = new Date();
    const monthsElapsed =
      rows[0]?.year === now.getFullYear()
        ? now.getMonth() + 1
        : 12;
    const inWindow = rows.filter(d => d.month <= monthsElapsed);
    const officeYtd = inWindow.reduce(
      (s, d) => s + d.officeSales,
      0,
    );
    const myYtd = inWindow.reduce((s, d) => s + d.mySales, 0);
    const expectedToDate = (yearlyTarget * monthsElapsed) / 12;
    let status: SalesStatus = 'none';
    if (yearlyTarget > 0) {
      const pace =
        expectedToDate > 0 ? officeYtd / expectedToDate : 1;
      if (pace >= 1.05) {
        status = 'ahead';
      } else if (pace >= 0.9) {
        status = 'on-track';
      } else {
        status = 'behind';
      }
    }
    const pctOfYear =
      yearlyTarget > 0
        ? Math.round((officeYtd / yearlyTarget) * 100)
        : null;
    const myShare =
      officeYtd > 0
        ? Math.round((myYtd / officeYtd) * 100)
        : null;
    return {
      year: rows[0]?.year ?? now.getFullYear(),
      yearlyTarget,
      officeYtd,
      pctOfYear,
      myShare,
      status,
    };
  }, [rows]);

  return (
    <Container>
      <Header>
        Monthly Income Chart
        <InfoHint
          arrow
          placement="top"
          title={MONTHLY_INCOME_CHART_DESCRIPTION}
        >
          <InfoIcon fontSize="small" />
        </InfoHint>
      </Header>

      {summary && (
        <StatusBanner>
          <BannerMain>
            <BannerEyebrow>
              {summary.year} Target Sales
            </BannerEyebrow>
            <BannerNumbers>
              <strong>
                {currencyFormat.format(summary.officeYtd)}
              </strong>{' '}
              of{' '}
              {currencyFormat.format(summary.yearlyTarget)}{' '}
              yearly target
              {summary.pctOfYear != null && (
                <Muted> · {summary.pctOfYear}% of goal</Muted>
              )}
              {summary.myShare != null && (
                <Muted> · your share {summary.myShare}%</Muted>
              )}
            </BannerNumbers>
          </BannerMain>
          <StatusBadge data-status={summary.status}>
            {STATUS_LABEL[summary.status]}
          </StatusBadge>
        </StatusBanner>
      )}

      <Bar>
        {hasData ? (
          <ResponsiveBar
            data={barData}
            keys={INCOME_KEYS}
            indexBy="label"
            groupMode="grouped"
            padding={0.2}
            innerPadding={1}
            borderRadius={2}
            colors={[theme.marker4, theme.marker1, theme.marker2]}
            enableLabel={false}
            enableGridY
            valueScale={{ type: 'linear', max: maxY }}
            margin={{ top: 24, right: 16, bottom: 36, left: 56 }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              format: shortCurrency,
            }}
            axisBottom={{ tickSize: 0, tickPadding: 6 }}
            theme={{
              grid: {
                line: {
                  stroke: '#E2E8F0',
                  strokeDasharray: '2 4',
                },
              },
              axis: {
                ticks: { text: { fill: '#64748B', fontSize: 11 } },
              },
              legends: { text: { fill: '#334155', fontSize: 12 } },
              tooltip: {
                container: {
                  background: '#ffffff',
                  color: '#111827',
                  fontSize: 12,
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                  padding: '8px 12px',
                },
              },
            }}
            tooltip={({ id, value, color }: any) => (
              <Tooltip>
                <TooltipDot
                  style={{ background: color as string }}
                />
                <TooltipLabel>{id}</TooltipLabel>
                <TooltipValue>
                  {currencyFormat.format(Number(value) || 0)}
                </TooltipValue>
              </Tooltip>
            )}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'top-right',
                direction: 'row',
                translateY: -20,
                translateX: 0,
                itemsSpacing: 14,
                itemWidth: 92,
                itemHeight: 18,
                symbolShape: 'square',
                symbolSize: 12,
                itemTextColor: '#334155',
              },
            ]}
          />
        ) : (
          <EmptyState>No income data available</EmptyState>
        )}
      </Bar>
    </Container>
  );
};

const Container = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 16px;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: var(--neutral-400);
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;
const Bar = styled.div`
  height: 400px;
  width: 100%;
`;
const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;
`;
const BannerMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const BannerEyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;
const BannerNumbers = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-700);

  > strong {
    font-size: 1rem;
    font-weight: 700;
    color: var(--mainText);
  }
`;
const Muted = styled.span`
  color: var(--neutral-500);
`;
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  background: var(--neutral-100);
  color: var(--neutral-600);
  border: 1px solid var(--border);

  &[data-status='ahead'] {
    background: #dcfce7;
    color: #15803d;
    border-color: #bbf7d0;
  }
  &[data-status='on-track'] {
    background: var(--primary-50);
    color: var(--primary-700);
    border-color: var(--primary-100);
  }
  &[data-status='behind'] {
    background: var(--warning-50, #fff6e0);
    color: var(--warning-700, #8a5f05);
    border-color: var(--warning-100, #ffe7ac);
  }
`;
const Tooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const TooltipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
`;
const TooltipLabel = styled.span`
  color: #475569;
`;
const TooltipValue = styled.span`
  color: #0f172a;
  font-weight: 700;
`;
const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

export default MonthlyIncomeChart;
